/**
 * Pilotage-section detection: after a route is decided, flag the stretches that
 * want closer pilotage so the assisted plan can offer to make pilotage plans for
 * them (reusing the clearing-bearing / transit tools).
 *
 * A leg is flagged when it is the departure or arrival leg (you leave and enter
 * close to land), when it shoals (charted depth drops below a threshold), or when
 * charted hazards sit near its track. Consecutive flagged legs merge into one
 * section. Pure + injectable so it's testable without the chart.
 *
 * Honest, like the rest: this leans on the same coarse charted-depth read and the
 * route hazard scan, so it's a prompt to look, not an authority on what's tricky.
 */
import { chartedDepthUnderBoat, type FeatureSource } from '@/safety';
import { rhumbBearingDeg, rhumbDistanceNm, rhumbDestination } from '@/nav';

export interface PilotageSection {
  /** Waypoint index the section starts at. */
  fromIndex: number;
  /** Waypoint index the section ends at (one past the last leg). */
  toIndex: number;
  /** Why it's flagged: 'departure' | 'arrival' | 'shoaling water' | 'charted hazards'. */
  reasons: string[];
  /** A representative point on the section, for centring the chart. */
  position: [number, number];
}

export interface DetectPilotageOpts {
  cells?: FeatureSource;
  /** Legs (0-based) the route hazard scan flagged. */
  hazardLegIndices?: number[];
  /** Charted depth at/under which a leg counts as close/shoaling water. */
  shoalDepthM?: number;
  /** Sampling step along a leg for the depth check, nm. */
  sampleNm?: number;
}

export function detectPilotageSections(
  waypoints: [number, number][],
  opts: DetectPilotageOpts = {},
): PilotageSection[] {
  const legCount = waypoints.length - 1;
  if (legCount < 1) return [];
  const shoal = opts.shoalDepthM ?? 8;
  const sampleNm = opts.sampleNm ?? 0.25;
  const hazardLegs = new Set(opts.hazardLegIndices ?? []);
  const cells = opts.cells;

  const legReasons: string[][] = [];
  for (let i = 0; i < legCount; i++) {
    const reasons: string[] = [];
    if (i === 0) reasons.push('departure');
    if (i === legCount - 1) reasons.push('arrival');
    if (hazardLegs.has(i)) reasons.push('charted hazards');
    if (cells) {
      const a = waypoints[i];
      const b = waypoints[i + 1];
      const dist = rhumbDistanceNm(a, b);
      const brg = rhumbBearingDeg(a, b);
      const steps = Math.max(1, Math.ceil(dist / sampleNm));
      let minDepth = Infinity;
      for (let s = 0; s <= steps; s++) {
        const p = rhumbDestination(a, brg, (dist * s) / steps);
        const d = chartedDepthUnderBoat(p, cells, 0.05);
        if (d != null && d < minDepth) minDepth = d;
      }
      if (minDepth < shoal) reasons.push('shoaling water');
    }
    legReasons.push(reasons);
  }

  const sections: PilotageSection[] = [];
  let i = 0;
  while (i < legCount) {
    if (legReasons[i].length === 0) {
      i++;
      continue;
    }
    let j = i;
    const reasons = new Set<string>();
    while (j < legCount && legReasons[j].length > 0) {
      for (const r of legReasons[j]) reasons.add(r);
      j++;
    }
    const midWp = waypoints[Math.floor((i + j) / 2)] ?? waypoints[i];
    // Order the reasons sensibly: departure, then shoaling/hazards, then arrival.
    const order = ['departure', 'shoaling water', 'charted hazards', 'arrival'];
    const ordered = order.filter((r) => reasons.has(r));
    sections.push({ fromIndex: i, toIndex: j, reasons: ordered, position: midWp });
    i = j;
  }
  return sections;
}

/** A short title for a section, e.g. "Departure", "Arrival", "Shoaling water". */
export function pilotageSectionTitle(s: PilotageSection): string {
  if (s.reasons.includes('departure') && s.reasons.includes('arrival')) return 'Whole passage';
  if (s.reasons.includes('departure')) return 'Departure';
  if (s.reasons.includes('arrival')) return 'Arrival';
  if (s.reasons.includes('shoaling water')) return 'Shoaling water';
  if (s.reasons.includes('charted hazards')) return 'Charted hazards';
  return 'Close quarters';
}
